import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react';

import { cn } from 'lib/helpers/utility';

import { TYPE_SIGNATURE_FONTS } from '../lib/constants';

import PlaceholderSignature from './PlaceholderSignature';

// Signature box padding — Figma 81-13279 uses 20px on all sides.
const BOX_PADDING = 20;
const VERTICAL_PADDING = BOX_PADDING;

const MIN_FONT_SIZE = 16;
// Design (Figma 81-13279): typed signature renders at 48px on the 200px-tall box.
const baseFontSize = (canvasHeight) => Math.min(Math.round(canvasHeight * 0.24), 48);

const measureGlyphHeight = (ctx, text, fontSize) => {
  const metrics = ctx.measureText(text);
  const ascent = metrics.actualBoundingBoxAscent ?? fontSize * 0.85;
  const descent = metrics.actualBoundingBoxDescent ?? fontSize * 0.3;
  return { ascent, descent, total: ascent + descent };
};

const fitFontSize = (ctx, text, fontFamily, maxTextWidth, maxTextHeight, startSize) => {
  let fontSize = startSize;
  ctx.font = `${fontSize}px ${fontFamily}`;

  while (ctx.measureText(text).width > maxTextWidth && fontSize > MIN_FONT_SIZE) {
    fontSize -= 2;
    ctx.font = `${fontSize}px ${fontFamily}`;
  }

  let metrics = measureGlyphHeight(ctx, text, fontSize);
  while (metrics.total > maxTextHeight && fontSize > MIN_FONT_SIZE) {
    fontSize -= 2;
    ctx.font = `${fontSize}px ${fontFamily}`;
    metrics = measureGlyphHeight(ctx, text, fontSize);
  }

  return { fontSize, metrics };
};

let sharedMeasureCtx = null;
const getMeasureCtx = () => {
  if (!sharedMeasureCtx) sharedMeasureCtx = document.createElement('canvas').getContext('2d');
  return sharedMeasureCtx;
};

const computeDisplayFontSize = (text, fontFamily, canvasWidth, canvasHeight) => {
  const startSize = baseFontSize(canvasHeight);
  if (!text?.trim() || !canvasWidth) return startSize;
  const maxTextWidth = canvasWidth - BOX_PADDING * 2;
  const maxTextHeight = canvasHeight - VERTICAL_PADDING * 2;
  const { fontSize } = fitFontSize(
    getMeasureCtx(),
    text,
    fontFamily,
    maxTextWidth,
    maxTextHeight,
    startSize
  );
  return fontSize;
};

const renderTextToPng = (text, fontFamily, canvasWidth, canvasHeight) => {
  if (!text?.trim() || !canvasWidth) return null;
  const canvas = document.createElement('canvas');
  const ctx = canvas.getContext('2d');

  canvas.width = canvasWidth;
  canvas.height = canvasHeight;

  const maxTextWidth = canvasWidth - BOX_PADDING * 2;
  const maxTextHeight = canvasHeight - VERTICAL_PADDING * 2;
  const { metrics } = fitFontSize(
    ctx,
    text,
    fontFamily,
    maxTextWidth,
    maxTextHeight,
    baseFontSize(canvasHeight)
  );

  ctx.clearRect(0, 0, canvasWidth, canvasHeight);
  ctx.fillStyle = '#000000';
  ctx.textBaseline = 'alphabetic';
  ctx.textAlign = 'center';
  const baselineY = (canvasHeight - metrics.total) / 2 + metrics.ascent;
  ctx.fillText(text, canvasWidth / 2, baselineY);

  return canvas.toDataURL('image/png');
};

const TypeSignature = ({
  active,
  onChange,
  canvasHeight = 200,
  containerWidth = 0,
  fontId,
  fontsReady,
  defaultName = '',
  handleRef,
  'data-qa': dataQa
}) => {
  const [name, setName] = useState(defaultName);
  const lastEmittedRef = useRef(null);
  const inputRef = useRef(null);

  useEffect(() => {
    setName((prev) => (prev === '' ? defaultName : prev));
  }, [defaultName]);

  const selectedFont = useMemo(
    () => TYPE_SIGNATURE_FONTS.find((font) => font.id === fontId) || TYPE_SIGNATURE_FONTS[0],
    [fontId]
  );

  const computePng = useCallback(
    (nextName = name) => {
      if (!containerWidth) return null;
      return renderTextToPng(nextName, selectedFont.family, containerWidth, canvasHeight);
    },
    [name, selectedFont, containerWidth, canvasHeight, fontsReady]
  );

  const emitPng = useCallback(
    (nextName = name) => {
      const png = computePng(nextName);
      if (png === lastEmittedRef.current) return;
      lastEmittedRef.current = png;
      onChange?.(png);
    },
    [computePng, name, onChange]
  );

  useEffect(() => {
    if (!handleRef) return undefined;
    handleRef.current = {
      getValue: () => computePng(),
      clear: () => {
        setName('');
        lastEmittedRef.current = null;
        onChange?.(null);
      }
    };
    return () => {
      if (handleRef) handleRef.current = null;
    };
  }, [handleRef, computePng, onChange]);

  useEffect(() => {
    if (!active) return undefined;
    emitPng();
    return undefined;
  }, [active, emitPng]);

  const handleNameChange = (event) => {
    const nextName = event.target.value;
    setName(nextName);
    if (active) emitPng(nextName);
  };

  const fontSize = useMemo(
    () => computeDisplayFontSize(name, selectedFont.family, containerWidth, canvasHeight),
    [name, selectedFont, containerWidth, canvasHeight]
  );

  return (
    <div
      className="relative flex min-w-0 items-center justify-center overflow-hidden rounded-md border border-dashed border-neutral-500 bg-primary-10 px-5"
      style={{ height: canvasHeight }}
      onClick={() => inputRef.current?.focus()}>
      <div className="flex w-full min-w-0 items-center justify-center pb-2 pt-2">
        <input
          ref={inputRef}
          type="text"
          size={1}
          value={name}
          onChange={handleNameChange}
          data-qa={active ? dataQa : undefined}
          className={cn(
            'border-0 border-b border-solid border-neutral-300 bg-transparent text-center',
            'text-black outline-none'
          )}
          style={{
            fontFamily: selectedFont.family,
            fontSize,
            lineHeight: 1.6,
            paddingTop: '0.2em',
            paddingBottom: '0.1em',
            caretColor: '#0BA5EC',
            width: 0,
            minWidth: 0,
            flex: '1 1 0',
            boxSizing: 'border-box'
          }}
        />
      </div>
      {!name && (
        <PlaceholderSignature canvasHeight={canvasHeight} fontFamily={selectedFont.family} />
      )}
    </div>
  );
};

export default TypeSignature;
